/**
 * The states every screen needs.
 *
 * Loading, empty, error and permission-denied are written once here and used
 * by every module, so a table that is still loading looks the same in HR as
 * it does in accounting — and so no module has to invent its own way of
 * saying "nothing here yet".
 */

import { AlertTriangle, Inbox, Loader2, Lock, RefreshCw } from 'lucide-react';
import { cn } from '@/lib/utils';

export function Spinner({ label, className }: { label?: string; className?: string }) {
  return (
    <div className={cn('flex items-center justify-center gap-2 py-10 text-muted-foreground', className)}>
      <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
      <span className="text-[15px]">{label ?? 'Loading…'}</span>
      <span className="sr-only" role="status">{label ?? 'Loading'}</span>
    </div>
  );
}

/** Skeleton rows, for tables where the shape of what is coming is known. */
export function TableSkeleton({ rows = 6, columns = 5 }: { rows?: number; columns?: number }) {
  return (
    <div className="space-y-2 py-2" aria-hidden>
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className="flex gap-3">
          {Array.from({ length: columns }).map((__, c) => (
            <div
              key={c}
              className="h-8 flex-1 animate-pulse rounded bg-muted"
              style={{ animationDelay: `${(r * columns + c) * 25}ms` }}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

export function EmptyState({
  title,
  message,
  action,
  icon: Icon = Inbox,
}: {
  title: string;
  message?: string;
  action?: React.ReactNode;
  icon?: React.ComponentType<{ className?: string }>;
}) {
  return (
    // An empty list is the moment someone most needs telling what the screen
    // is for, so it is given the same weight as anything else on the page
    // rather than a faint dashed outline.
    <div className="flex flex-col items-center justify-center rounded-xl border border-border bg-muted/30 px-6 py-14 text-center">
      <span className="mb-3 grid h-11 w-11 place-items-center rounded-full bg-background shadow-sm">
        <Icon className="h-5 w-5 text-muted-foreground" aria-hidden />
      </span>
      <p className="text-[15px] font-semibold">{title}</p>
      {message && <p className="mt-1 max-w-md text-[15px] text-muted-foreground">{message}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

/**
 * Something failed.
 *
 * Shows what actually went wrong rather than a generic apology, and offers a
 * retry — most failures here are a dropped connection, not a broken feature.
 */
export function ErrorState({
  title = 'Something went wrong',
  error,
  onRetry,
}: {
  title?: string;
  error?: unknown;
  onRetry?: () => void;
}) {
  const message = error instanceof Error
    ? error.message
    : typeof error === 'string' ? error : null;

  return (
    <div className="flex flex-col items-center justify-center rounded-lg border border-destructive/25 bg-destructive/[0.04] px-6 py-12 text-center">
      <AlertTriangle className="mb-3 h-8 w-8 text-destructive/70" aria-hidden />
      <p className="text-[14px] font-medium">{title}</p>
      {message && <p className="mt-1 max-w-lg text-[15px] text-muted-foreground">{message}</p>}
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="mt-4 inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-3 py-1.5 text-[15px] font-medium transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <RefreshCw className="h-3.5 w-3.5" /> Try again
        </button>
      )}
    </div>
  );
}

/**
 * The user is signed in but not allowed here.
 *
 * Deliberately distinct from "not found": telling someone the screen does not
 * exist when it does is confusing, and telling them nothing is worse.
 */
export function PermissionDenied({ what }: { what?: string }) {
  return (
    <EmptyState
      icon={Lock}
      title="You do not have access to this"
      message={
        what
          ? `Your role does not include permission to view ${what}. An administrator can grant it under Administration → Roles & permissions.`
          : 'Your role does not include permission for this screen. An administrator can grant it under Administration → Roles & permissions.'
      }
    />
  );
}
